import {Button, Grid, Link, TextField, Typography} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {T, useT} from 'react-polyglot-hooks';
import {Redirect} from 'react-router';
import {Link as RouterLink} from 'react-router-dom';
import {useLastLocation} from 'react-router-last-location';
import useReactRouter from 'use-react-router';
import validate from 'validate.js';
import {LoadingOverlay} from '../../components/LoadingOverlay';
import {cleanCellphone} from '../../components/PhoneInput/PhoneInput';
import {getMe} from '../../graphql/auth';
import anyLoading from '../../utils/anyLoading';
import {useStyles} from './Login.style';
import loginUser from './loginUser.api';
import registerUser from './registerUser.api';

const LogIn = () => {
  //react hooks
  const {history, location} = useReactRouter();
  const lastLocation = useLastLocation();
  //graphql data
  const userData = getMe();
  const [sendLoginUser, loginData] = loginUser();
  const [sendRegisterUser, registerData] = registerUser();
  //ordinary vars
  const loggedIn = !!(userData.data && userData.data.me);
  const pageLoading = anyLoading(userData);
  const loginLoading = anyLoading(loginData, registerData);
  const classes = useStyles();
  const params = new URLSearchParams(location.search);
  const messageId = params.get('message');
  const {enqueueSnackbar} = useSnackbar();
  const t = useT();

  let message;
  switch(messageId){
    case '1':
      message = t('Your password has been updated. Please login.').toString();
      break;
    case '2':
      message = t('There was an error updating your password. Please try again.').toString();
      break;
    case '3':
      message = t('This password reset request has expired. Please try again.').toString();
      break;
  }

  if (!pageLoading && loggedIn) {
    return (<Redirect to="/dashboard"/>);
  }

  const loginSchema = {
    email: {
      presence: {allowEmpty: false, message: t('is required')},
      email: true,
      length: {
        maximum: 64
      }
    },
    password: {
      password: true,
    }
  };

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      password_confirmation: '',
      email_opt_out: false,
      sms_opt_out: false,
      cellphone: '',
      cellphone_code: '',
    },
    touched: {
      email: false,
      first_name: false,
      last_name: false,
      password: false,
      password_confirmation: false,
      email_opt_out: false,
      sms_opt_out: false,
      cellphone: false,
      cellphone_code: false,
    },
    errors: {
      email: null,
      first_name: null,
      last_name: null,
      password: null,
      password_confirmation: null,
      email_opt_out: null,
      sms_opt_out: null,
      cellphone: null,
      cellphone_code: null,
    }
  });

  useEffect(() => {
    const errors = validate(sanitiseFormValues(), loginSchema );

    setFormState(state => ({
      ...state,
      isValid: !errors,
      errors: errors || {}
    }));
  }, [formState.values]);

  const handleChange = (event: any) => {
    event.persist();

    setFormState(state => ({
      ...state,
      values: {
        ...state.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      },
      touched: {
        ...state.touched,
        [event.target.name]: true
      }
    }));
  };

  const hasError = (field: string) => {
    // @ts-ignore
    return !!(formState.touched[field] && formState.errors[field]);
  };

  const sanitiseFormValues = () => {
    return {
      email: formState.values.email,
      first_name: formState.values.first_name,
      last_name: formState.values.last_name,
      password: formState.values.password,
      password_confirmation: formState.values.password_confirmation,
      email_opt_out: formState.values.email_opt_out,
      sms_opt_out: formState.values.sms_opt_out,
      cellphone: cleanCellphone(formState.values.cellphone),
      cellphone_code: formState.values.cellphone_code,
    }
  };

  const onFormSubmit = (event: any) => {
    event.preventDefault();

    const onLogin = () => {
      const exemptUrls = ['/password/forgot', '/password/reset', '/email/verify'];
      const defaultUrl = '/dashboard';
      const goto: string = !lastLocation || exemptUrls.filter((url: string) => lastLocation.pathname.indexOf(url) === 0).length > 0 ? defaultUrl : `${lastLocation.pathname}${lastLocation.search}${lastLocation.hash}`;
      history.push(goto);
    };

    sendLoginUser({
      variables: {
        username: formState.values.email,
        password: formState.values.password,
      }
    })
      .then(onLogin)
      .catch(() => {
        enqueueSnackbar(t('There was an error logging in.'), {variant: 'error'});
      });
  };

  return (
    <div className={classes.root}>
      <Grid container className={classes.grid}>
        <Grid item className={classes.quoteContainer} lg={5} md={false}>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <Typography className={classes.quoteText} variant="h1">
                {/*<T phrase="What a lovely inspirational image. It makes me want to log in." />*/}
              </Typography>
              <div className={classes.person}>
                <Typography className={classes.name} variant="body1">
                    {/*<T phrase="James Tann" />*/}
                </Typography>
                <Typography className={classes.bio} variant="body2">
                    {/*<T phrase="Tilt"/>*/}
                </Typography>
              </div>
            </div>
          </div>
        </Grid>

        <Grid item className={classes.contentContainer} lg={7} md={12}>
          <Grid container alignItems="center" style={{height: '100%'}} >
            <LoadingOverlay loading={loginLoading.loading}>
            <div className={classes.content}>
              <div className={classes.contentHeader} />
              <div className={classes.contentBody}>
                <form className={classes.form} onSubmit={onFormSubmit}>
                  <Typography className={classes.title} variant="h2">
                    {t('Sign in')}
                  </Typography>

                  {message && (
                      <Typography color="primary" className={classes.title} variant="h4">
                        {message}
                      </Typography>
                  )}

                  <TextField
                    className={classes.textField}
                    error={hasError('email')}
                    fullWidth
                    helperText={
                      hasError('email') ? formState.errors.email![0] : null
                    }
                    label={t('Email address')}
                    name="email"
                    onChange={handleChange}
                    type="text"
                    value={formState.values!.email! || ''}
                    variant="outlined"
                  />

                  <TextField
                    className={classes.textField}
                    error={hasError('password')}
                    fullWidth
                    helperText={
                      hasError('password') ? formState.errors.password![0] : null
                    }
                    label={t('Password')}
                    name="password"
                    onChange={handleChange}
                    type="password"
                    value={formState.values.password || ''}
                    variant="outlined"
                  />

                  <Button
                    className={classes.signInButton}
                    color="primary"
                    disabled={!formState.isValid}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {t('Sign in')}
                  </Button>

                  <Typography
                      color="textSecondary"
                      variant="body1"
                  >
                    <T phrase="Forgot your password?" />&nbsp;
                    <Link color="textPrimary" variant="h6" component={RouterLink} to="/password/forgot"><T phrase="Reset"/></Link>
                  </Typography>
                </form>
              </div>
            </div>
            </LoadingOverlay>
          </Grid>

        </Grid>
      </Grid>
    </div>
  );
};

export default LogIn;
